import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ModalProvider, useModal } from './context/ModalContext.js';
import { AuthProvider, useAuth } from './context/AuthContext.js';
import { SocketProvider } from './context/SocketContext.js';
import { ToastProvider } from './context/ToastContext.js';
import { ThemeProvider } from './context/ThemeContext.js';
import { GoogleOAuthProvider } from '@react-oauth/google';
import Navbar from './components/Navbar.js';
import Hero from './components/Hero.js';
import HowItWorks from './components/HowItWorks.js';
import Features from './components/Features.js';
import SuccessStories from './components/SuccessStories.js';
import CTA from './components/CTA.js';
import Footer from './components/Footer.js';

import Modal from './components/Modal.js';
import LoginForm from './components/LoginForm.js';
import SignupForm from './components/SignupForm.js';
import ForgotPassword from './pages/auth/ForgotPassword.js';
import ResetPassword from './pages/auth/ResetPassword.js';

import AppPrivacyPolicyPage from './pages/AppPrivacyPolicyPage.js';
import AppTermsPage from './pages/AppTermsPage.js';
import AppSafetyStandardsPage from './pages/AppSafetyStandardsPage.js';
import AppDeleteAccountPage from './pages/AppDeleteAccountPage.js';
import AppVerifyDeletionPage from './pages/AppVerifyDeletionPage.js';
import DeletionRequests from './pages/admin/DeletionRequests.js';
import DashboardHome from './pages/dashboard/DashboardHome.js';
import Profiles from './pages/dashboard/Profiles.js';
import Messages from './pages/dashboard/Messages.js';
import MyMatches from './pages/dashboard/MyMatches.js';
import Profile from './pages/dashboard/Profile.js';
import UserProfile from './pages/dashboard/UserProfile.js';

import Settings from './pages/dashboard/Settings.js';
import DashboardLayout from './components/dashboard/DashboardLayout.js';
import Posts from './pages/dashboard/Posts.js';
import AdminDashboard from './pages/admin/AdminDashboard.js';
import UserManagement from './pages/admin/UserManagement.js';
import Analytics from './pages/admin/Analytics.js';


import Security from './pages/admin/Security';
import SystemSettings from './pages/admin/SystemSettings.js';


import AdminReports from './pages/admin/AdminReports.js';

// Admin Route Component
const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500"></div>
      </div>
    );
  }

  // Check if user is admin based on email
  const isAdmin = user?.email?.toLowerCase().includes('admin@');

  if (!isAdmin) {
    console.log('Access denied - User is not an admin:', user?.email);
    return <Navigate to="/dashboard/posts" replace />;
  }

  return children;
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-romantic-500"></div>
      </div>
    );
  }

  return user ? children : <Navigate to="/" />;
};

// Public Route Component (redirect to dashboard if logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-romantic-500"></div>
      </div>
    );
  }

  return !user ? children : <Navigate to="/dashboard/profiles" />;
};

// Landing Page Component
const LandingPage = () => {
  const { isLoginOpen, isSignupOpen, closeModals } = useModal();

  return (
    <div className="min-h-screen landing-shell">
      <Navbar />
      <Hero />
      <HowItWorks />
      <Features />
      <SuccessStories />
      <CTA />
      <Footer />


      <Modal isOpen={isLoginOpen} onClose={closeModals}>
        <LoginForm />
      </Modal>

      <Modal isOpen={isSignupOpen} onClose={closeModals}>
        <SignupForm />
      </Modal>


    </div>
  );
};

function App() {
  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

  return (
    <GoogleOAuthProvider clientId={googleClientId}>
      <ThemeProvider>
      <AuthProvider>
        <ToastProvider>
          <SocketProvider>
            <ModalProvider>
              <Router>
                <Routes>
                  {/* Public Privacy Policy Route for App Store - MOVED TO TOP FOR PRIORITY */}
                  <Route path="/app/privacy" element={<AppPrivacyPolicyPage />} />
                  <Route path="/app/terms" element={<AppTermsPage />} />
                  <Route path="/app/safety-standards" element={<AppSafetyStandardsPage />} />
                  <Route path="/app/delete-account" element={<AppDeleteAccountPage />} />
                  <Route path="/app/delete-verify" element={<AppVerifyDeletionPage />} />

                  <Route path="/" element={
                    <PublicRoute>
                      <LandingPage />
                    </PublicRoute>
                  } />

                  {/* Dashboard Layout with nested routes */}
                  <Route path="/dashboard" element={
                    <ProtectedRoute>
                      <DashboardLayout />
                    </ProtectedRoute>
                  }>
                    <Route index element={<DashboardHome />} />
                    <Route path="posts" element={<Posts />} />
                    <Route path="profiles" element={<Profiles />} />
                    <Route path="messages" element={<Messages />} />
                    <Route path="matches" element={<MyMatches />} />
                    <Route path="profile" element={<Profile />} />
                    <Route path="user-profile/:userId" element={<UserProfile />} />

                    <Route path="settings" element={<Settings />} />
                  </Route>

                  {/* Auth Routes */}
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/reset-password" element={<ResetPassword />} />

                  {/* Admin Routes */}
                  <Route path="/admin" element={
                    <AdminRoute>
                      <Navigate to="/admin/dashboard" replace />
                    </AdminRoute>
                  } />
                  <Route path="/admin/dashboard" element={
                    <AdminRoute>
                      <AdminDashboard />
                    </AdminRoute>
                  } />
                  <Route path="/admin/analytics" element={
                    <AdminRoute>
                      <Analytics />
                    </AdminRoute>
                  } />
                  <Route path="/admin/users" element={
                    <AdminRoute>
                      <UserManagement />
                    </AdminRoute>
                  } />
                  <Route path="/admin/content" element={
                    <AdminRoute>
                      <AdminReports />
                    </AdminRoute>
                  } />
                  <Route path="/admin/deletion-requests" element={
                    <AdminRoute>
                      <DeletionRequests />
                    </AdminRoute>
                  } />

                  <Route path="/admin/message-reports" element={
                    <AdminRoute>
                      <AdminReports />
                    </AdminRoute>
                  } />
                  <Route path="/admin/security" element={
                    <AdminRoute>
                      <Security />
                    </AdminRoute>
                  } />
                  <Route path="/admin/settings" element={
                    <AdminRoute>
                      <SystemSettings />
                    </AdminRoute>
                  } />
                  <Route path="*" element={<Navigate to="/" />} />
                </Routes>
              </Router>
            </ModalProvider>
          </SocketProvider>
        </ToastProvider>
      </AuthProvider>
      </ThemeProvider>
    </GoogleOAuthProvider>
  );
}

export default App;